import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { trackPerformanceEvent } from "../../utils/analytics/googleAnalytics";

const roundMilliseconds = (value) => Math.max(0, Math.round(Number(value) || 0));
const roundKilobytes = (value) => Math.max(0, Math.round((Number(value) || 0) / 1024));

const getResourceKind = (entry) => {
  if (entry.initiatorType === "img") return "image";
  if (String(entry.name || "").includes("/api/")) return "api";
  return null;
};

const getApiPath = (resourceUrl) => {
  try {
    return new URL(resourceUrl, window.location.origin).pathname
      .replace(/\/\d+(?=\/|$)/g, "/:id")
      .replace(/\/[a-zA-Z0-9_-]{16,}(?=\/|$)/g, "/:id");
  } catch {
    return "/api/unknown";
  }
};

const PerformanceTelemetry = ({ enabled }) => {
  const location = useLocation();
  const routeStartedAtRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !window.performance) return undefined;

    const navigationEntry = performance.getEntriesByType("navigation")[0];
    if (navigationEntry) {
      trackPerformanceEvent("rum_navigation", {
        duration_ms: roundMilliseconds(navigationEntry.duration),
        ttfb_ms: roundMilliseconds(navigationEntry.responseStart - navigationEntry.requestStart),
        dom_content_loaded_ms: roundMilliseconds(navigationEntry.domContentLoadedEventEnd),
        load_ms: roundMilliseconds(navigationEntry.loadEventEnd),
      });
    }

    let largestContentfulPaint = 0;
    let cumulativeLayoutShift = 0;
    let interactionToNextPaint = 0;
    const resourceStats = { imageCount: 0, imageBytes: 0, apiCount: 0, apiBytes: 0 };
    const observedResources = new Set();

    const observeResource = (entry) => {
      const key = `${entry.name}:${entry.startTime}`;
      if (observedResources.has(key)) return;
      observedResources.add(key);

      const kind = getResourceKind(entry);
      if (kind === "image") {
        resourceStats.imageCount += 1;
        resourceStats.imageBytes += Number(entry.encodedBodySize || entry.transferSize || 0);
      }
      if (kind === "api") {
        resourceStats.apiCount += 1;
        resourceStats.apiBytes += Number(entry.encodedBodySize || entry.transferSize || 0);
        if (entry.duration >= 400) {
          trackPerformanceEvent("rum_slow_api", {
            path: getApiPath(entry.name),
            duration_ms: roundMilliseconds(entry.duration),
            ttfb_ms: roundMilliseconds(entry.responseStart - entry.requestStart),
            transfer_kb: roundKilobytes(entry.encodedBodySize || entry.transferSize),
          });
        }
      }
    };

    performance.getEntriesByType("resource").forEach(observeResource);

    const observers = [];
    const observe = (type, callback) => {
      try {
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach(callback);
        });
        observer.observe({ type, buffered: true });
        observers.push(observer);
      } catch {
        // Some browsers do not expose every PerformanceObserver entry type.
      }
    };

    observe("largest-contentful-paint", (entry) => {
      largestContentfulPaint = Math.max(largestContentfulPaint, Number(entry.startTime || 0));
    });
    observe("layout-shift", (entry) => {
      if (!entry.hadRecentInput) cumulativeLayoutShift += Number(entry.value || 0);
    });
    observe("event", (entry) => {
      interactionToNextPaint = Math.max(
        interactionToNextPaint,
        Number(entry.duration || 0)
      );
    });
    observe("resource", observeResource);

    const flush = () => {
      trackPerformanceEvent("rum_web_vitals", {
        lcp_ms: roundMilliseconds(largestContentfulPaint),
        cls_milli: Math.round(cumulativeLayoutShift * 1000),
        inp_ms: roundMilliseconds(interactionToNextPaint),
      });
      trackPerformanceEvent("rum_resources", {
        image_count: resourceStats.imageCount,
        image_kb: roundKilobytes(resourceStats.imageBytes),
        api_count: resourceStats.apiCount,
        api_kb: roundKilobytes(resourceStats.apiBytes),
      });
    };

    window.addEventListener("pagehide", flush, { once: true });
    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener("pagehide", flush);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const startedAt = performance.now();
    routeStartedAtRef.current = startedAt;
    let frameId = window.requestAnimationFrame(() => {
      frameId = window.requestAnimationFrame(() => {
        if (routeStartedAtRef.current !== startedAt) return;
        trackPerformanceEvent("rum_route_settle", {
          path: location.pathname,
          duration_ms: roundMilliseconds(performance.now() - startedAt),
          has_query: Boolean(location.search),
        });
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [enabled, location.pathname, location.search]);

  return null;
};

export default PerformanceTelemetry;
